import { DialogComponent } from '@theme/dialog';

/**
 * A custom element that manages the PLP SEO drawer's tabs (comparison, key features, FAQ).
 * Each trigger opens the shared drawer directly on its own tab; the in-drawer tablist
 * switches between tabs without closing the drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} tabsList - The scrollable tab strip (a slider on mobile).
 * @property {HTMLButtonElement[]} tabs - The in-drawer tab buttons.
 * @property {HTMLElement[]} panels - The tab panels.
 *
 * @extends {DialogComponent<Refs>}
 */
class CollectionSeoDrawer extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('dialog:open', this.#focusDialog);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('dialog:open', this.#focusDialog);
  }

  /**
   * showModal() auto-focuses the first focusable descendant (the first tab button),
   * which shows a focus-visible ring even though the user just tapped a trigger
   * elsewhere. Redirect initial focus to the dialog itself instead.
   */
  #focusDialog = () => {
    const { dialog } = this.refs;
    dialog.setAttribute('tabindex', '-1');
    dialog.focus({ preventScroll: true });
  };

  /**
   * Activates a tab and opens the drawer (a no-op if it's already open).
   *
   * @param {{tab: string}} data
   */
  activateTab({ tab }) {
    const { tabs, tabsList, panels } = this.refs;
    const isTabsListScrollable = Boolean(tabsList && tabsList.scrollWidth > tabsList.clientWidth);

    tabs?.forEach((button) => {
      const isActive = button.dataset.tab === tab;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.tabIndex = isActive ? 0 : -1;

      if (isActive && isTabsListScrollable) {
        button.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    });

    panels?.forEach((panel) => {
      panel.hidden = panel.dataset.tab !== tab;
    });

    this.showDialog();
  }
}

if (!customElements.get('collection-seo-drawer')) {
  customElements.define('collection-seo-drawer', CollectionSeoDrawer);
}
